///
/// EarthBrowser
///
/// Created by Matt Giger on 4/8/2024
/// Copyright (c) 2025 Geodata Labs
///

import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { type CameraProjection } from "./Camera"

export const cCameraFOV = 40.0

export class Perspective implements CameraProjection {
	public fov: number
	public near: number
	public far: number
	public width: number
	public height: number
	/**
	 * Lens shift in NDC units: where the view axis lands on screen (0 = the
	 * center). Lets the host center the scene inside the part of the canvas
	 * its chrome leaves visible (e.g. -0.3 when a side panel covers the
	 * right 30%) without touching the camera pose. The shift is baked into
	 * the matrix, so worldToScreen / rayFrom stay consistent with it.
	 */
	public offsetX = 0
	public offsetY = 0

	constructor(fov: number = cCameraFOV, near: number = 0.01, far: number = 1000, width: number = 1, height: number = 1) {
		this.fov = fov
		this.near = near
		this.far = far
		this.width = width
		this.height = height
	}

	public setSize(width: number, height: number): void {
		this.width = width
		this.height = height
	}

	public get projection(): matrix {
		const znear = this.near
		const zfar = this.far
		const aspect = this.width / this.height
		const n = 1.0 / (this.near - this.far)
		const f = 1.0 / Math.tan(this.fov * 0.5 * trig.rad)

		// Column-major: the third column multiplies eye z (= -w_clip), so a
		// -offset there adds +offset to NDC x / y after the divide
		return new matrix([
			f/aspect,		0,				0,					0,
			0,				f,				0,					0,
			-this.offsetX,	-this.offsetY,	(zfar+znear)*n,		-1,
			0,				0,				2*zfar*znear*n,		0
		])
	}
}